import React from 'react';
import { Helmet } from 'react-helmet-async';

import { Button } from 'common/Button/Button';
import { ButtonGroup, ButtonGroupItem } from '@99designs/common/components/ButtonGroup';
import { formatMessage } from '@99designs/common/i18n';

import { DesignFigure } from 'common/DesignFigure';
import Heading from 'libs/core-components/Heading/Heading';
import ContentBody from 'common/Layout/ContentBody';
import SubtleText from 'common/SubtleText';

import Poopskies from 'images/baby_figzy88.jpg';
import LostDog from 'images/dog_alinasink.jpg';
import Elephant from 'images/elephant_ganjarelex.jpg';
import Page from 'Page';
import useRouter from 'common/Routing/useRouter';
import { useAppConfig } from 'common/AppConfigContext';

interface Props {
    errorCode: ErrorCodeKeys;
    error?: { message: string };
}

function FullscreenError({ errorCode, error }: Props) {
    const { displayErrors } = useAppConfig();
    const { staticContext } = useRouter();
    if (staticContext) {
        staticContext.statusCode = Number(errorCode);
    }

    return (
        <Page title={contentByErrorCode[errorCode].heading}>
            <Helmet
                bodyAttributes={{
                    class: 'max--desktop transparent-site-header',
                }}
            />
            <ContentBody>
                <div className="fullscreen-error">
                    <div className="fullscreen-error__details">
                        <Heading
                            accessibilityLevel={1}
                            withDivider
                            style={{ color: contentByErrorCode[errorCode].color }}
                            LEGACY_marginBottom="textLarge"
                        >
                            {contentByErrorCode[errorCode].heading}
                        </Heading>
                        <p>{contentByErrorCode[errorCode].message}</p>
                        {error && displayErrors ? (
                            <SubtleText component="small">{error.message}</SubtleText>
                        ) : null}
                    </div>
                    <div className="fullscreen-error__artwork">
                        <DesignFigure
                            imageSource={contentByErrorCode[errorCode].image}
                            attribution={formatMessage(__('by {designer}'), {
                                designer: contentByErrorCode[errorCode].imageAttribution,
                            })}
                            variations={['attribution-right']}
                        />
                    </div>
                    <div className="fullscreen-error__actions">
                        <ButtonGroup variations={['responsive']}>
                            <ButtonGroupItem>
                                <Button
                                    href="/"
                                    variations={['jumbo', 'fluid', 'primary']}
                                    style={{
                                        backgroundColor: contentByErrorCode[errorCode].color,
                                        borderColor: contentByErrorCode[errorCode].color,
                                    }}
                                >
                                    {__('Get me outta here')}
                                </Button>
                            </ButtonGroupItem>
                            <ButtonGroupItem>
                                <Button
                                    href="/help"
                                    variations={['jumbo', 'fluid', 'secondary']}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderColor: contentByErrorCode[errorCode].color,
                                        color: contentByErrorCode[errorCode].color,
                                    }}
                                >
                                    {__('Help')}
                                </Button>
                            </ButtonGroupItem>
                        </ButtonGroup>
                    </div>
                </div>
            </ContentBody>
        </Page>
    );
}

export default FullscreenError;

type ErrorCodeKeys = '403' | '404' | '500';

type ErrorContentByCodeType = {
    [key in ErrorCodeKeys]: {
        message: string;
        heading: string;
        image: string;
        imageAttribution: string;
        color: string;
    };
};

const contentByErrorCode: ErrorContentByCodeType = {
    403: {
        heading: __('Poopskies'),
        message: __('This is not where I want to be!'),
        image: Poopskies,
        imageAttribution: 'Figzy88',
        color: '#4DC0E3',
    },
    404: {
        heading: __('Lost your way?'),
        message: __('This is not where I want to be!'),
        image: LostDog,
        imageAttribution: 'ALINAsINK',
        color: '#F36C78',
    },
    500: {
        heading: __('Eeeeeeek!'),
        message: __(
            'It seems there was a problem. The 99designs development team has been notified and are likely addressing the problem at this very minute. Sit tight, grab a coffee, and hit refresh in a few minutes.'
        ),
        image: Elephant,
        imageAttribution: 'ganjarelex',
        color: '#C1B946',
    },
};
